import { watch, onBeforeUnmount } from 'vue'

// Module-level stack of open overlays (modals AND sliding panels), shared
// across all instances, so stacked overlays close one at a time.
//
// Escape is handled by ONE shared document listener that asks only the
// topmost overlay to close. Per-instance listeners are unsafe here: for a
// trusted keypress the browser runs a microtask checkpoint BETWEEN listeners,
// so Vue flushes watchers, the just-closed overlay leaves the stack, and the
// next listener in line sees itself topmost — one physical Esc press would
// peel the picker AND the form under it.
const overlays = []

function onDocumentKeydown(event) {
  if (event.key !== 'Escape') return
  // A held Escape auto-repeats; each stack layer must cost a distinct press.
  if (event.repeat) return
  overlays[overlays.length - 1]?.onEscape?.()
}

function syncListener() {
  if (overlays.length) {
    document.addEventListener('keydown', onDocumentKeydown)
  } else {
    document.removeEventListener('keydown', onDocumentKeydown)
  }
}

// Reactive mode: pass a watchable `isOpen` source and stack membership follows
// it. Manual mode: pass null and call enter()/leave() yourself — for hosts
// whose close work must run while they are still on the stack (AppSlidingPanel
// restores focus BEFORE leaving). Both modes leave the stack on unmount.
// `onEscape` is invoked when Escape is pressed while this overlay is topmost.
export default function useModalStack(isOpen = null, { onEscape = null } = {}) {
  const entry = { onEscape }

  function enter() {
    if (overlays.includes(entry)) return
    overlays.push(entry)
    syncListener()
  }

  function leave() {
    const index = overlays.indexOf(entry)
    if (index >= 0) overlays.splice(index, 1)
    syncListener()
  }

  if (isOpen) {
    watch(isOpen, (open) => {
      if (open) enter()
      else leave()
    }, { immediate: true })
  }

  onBeforeUnmount(leave)

  return {
    isTopmost: () => overlays[overlays.length - 1] === entry,
    enter,
    leave
  }
}
